/**
 *
 * @param diff  If you want to get a year on the past or future, you can pass a number to
 *
 * @returns
 */
export const getCurrentYear = (diff = 0) => new Date().getFullYear() - diff;


/**
 * Shared core for rendering a date string as "<month> <day>, <year>, <hour>:<minute> <zone>" parts.
 * Callers pick the month style and assemble their own separators/wording around these parts.
 *
 * @param dateString  A string representing a date
 * @param timeZone    IANA zone; defaults to the viewer's zone
 * @param monthStyle  `Intl.DateTimeFormat`'s month style, e.g. 'long' or 'short'
 */
export const formatTimestampParts = (dateString: string, timeZone: string | undefined, monthStyle: 'long' | 'short') => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: monthStyle,
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).formatToParts(new Date(dateString));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';

  return { month: part('month'), day: part('day'), year: part('year'), hour: part('hour'), minute: part('minute'), zone: part('timeZoneName') };
};

/**
 *
 * @param dateString  A string representing a date
 * @param timeZone    IANA zone; defaults to the viewer's zone
 *
 * @returns A string formatted as "Month Day, Year at HH:MM ZONE"
 */
export const formatDateAndHour = (dateString: string, timeZone?: string) => {
  const { month, day, year, hour, minute, zone } = formatTimestampParts(dateString, timeZone, 'long');
  return `${month} ${day}, ${year} at ${hour}:${minute} ${zone}`;
};

/**
 * Format the amount to the currency format
 *
 * @param amount  The amount to format
 * @param fractionDigits  The number of fraction digits to display
 * @returns The formatted amount
 */
export const formatMoney = (amount: number, fractionDigits = 0) => new Intl.NumberFormat(
  'en-CA',
  {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: fractionDigits
  }
).format(amount)

/**
 * Format the string from camel case to title case
 *
 * @param str The string to format
 * @returns The formatted string
 * @example
 * camel2Title('creditReason') // 'Credit Reason'
 * camel2Title('creditReasonOther') // 'Credit Reason Other'
 * camel2Title('creditReasonOtherOther') // 'Credit Reason Other Other'
 */
export const camel2Title = (str: string) => str
  .replace(/([A-Z])/g, (match) => ` ${match}`)
  .replace(/^./, (match) => match.toUpperCase())
  .trim();

/**
 * Capitalize the first letter of the string
 * @param str The string to capitalize
 * @returns The capitalized string
 * @example
 * capitalizeFirstLetter('credit') // 'Credit'
 */
export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Delay the execution of the function
 * @param ms The number of milliseconds to delay
 * @returns A promise that resolves after the delay
 * @example
 * delay(1000) // Waits for 1 second
 * delay(0) // Waits for 0 seconds
 * delay(-100) // Waits for 0 seconds
 */
export const delay = (ms: number) => new Promise((resolve) => {
  if (Number.isNaN(ms) || ms < 0 || !ms) {
    resolve(undefined);
  } else {
    setTimeout(resolve, ms);
  }
});

const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/** Neutralises spreadsheet formula injection (OWASP): a leading `=`, `+`, `-`, `@`, tab or CR gets a `'` prefix. */
export const escapeCsvCell = (value: string | null | undefined): string => {
  const text = value ?? '';
  return FORMULA_TRIGGER.test(text) ? `'${text}` : text;
};